/**
 * Product Placeholder Images
 *
 * Provides brand-specific placeholder images for products when no image is available.
 * Used in product cards on PLPs, sliders, cart items, etc.
 */

import {
  getActiveBrandProductPlaceholders,
  type PlaceholderProductType,
} from '@/lib/brands/productPlaceholders';

export type { PlaceholderProductType };

/**
 * Get the placeholder image path for a product type.
 *
 * @param productType - The type of product (watch, strap, buckle, eyewear, sunglasses)
 * @returns The path to the appropriate placeholder image
 */
export function getProductPlaceholder(productType?: string | null): string {
  const placeholders = getActiveBrandProductPlaceholders();

  if (!productType) {
    return placeholders.default;
  }

  const normalizedType = productType.toLowerCase() as PlaceholderProductType;

  const typeMap: Record<string, PlaceholderProductType> = {
    watch: 'watch',
    watches: 'watch',
    strap: 'strap',
    straps: 'strap',
    buckle: 'buckle',
    buckles: 'buckle',
    eyewear: 'eyewear',
    sunglasses: 'sunglasses',
    sunglass: 'sunglasses',
  };

  const mappedType = typeMap[normalizedType];

  return placeholders[mappedType] || placeholders.default;
}

export function isValidImageUrl(imageUrl?: string | null): boolean {
  if (!imageUrl) return false;
  if (imageUrl.trim() === '') return false;

  const invalidPatterns = [
    'placeholder',
    'no-image',
    'noimage',
    'missing',
    'default.svg',
    'default.png',
  ];

  const lowerUrl = imageUrl.toLowerCase();
  return !invalidPatterns.some((pattern) => lowerUrl.includes(pattern));
}

export function getImageOrPlaceholder(
  imageUrl?: string | null,
  productType?: string | null
): string {
  if (isValidImageUrl(imageUrl)) {
    return imageUrl!;
  }
  return getProductPlaceholder(productType);
}
